/**
 * IMA SDK Loader
 */

type LoadingStatus = 'idle' | 'loading' | 'loaded' | 'error';

export class ImaSdkLoader {
	private static _status: LoadingStatus = 'idle';
	private static _loadPromise: Promise<void> | null = null;
	private static readonly SDK_URL = 'https://imasdk.googleapis.com/js/sdkloader/ima3.js';

	static get status(): LoadingStatus {
		return ImaSdkLoader._status;
	}

	/**
	 * Load the IMA SDK script.
	 */
	static async loadSdk(): Promise<void> {
		if (this._loadPromise) {
			return this._loadPromise;
		}

		if (this._status === 'loaded') {
			return Promise.resolve();
		}

		if (this._status === 'error') {
			return Promise.reject(new Error('IMA SDK previously failed to load'));
		}

		this._status = 'loading';
		this._loadPromise = this._loadScript();

		try {
			await this._loadPromise;
			this._status = 'loaded';
		} catch (error) {
			this._status = 'error';
			throw error;
		}
	}

	/**
	 * Load script tag and inject into DOM.
	 */
	private static _loadScript(): Promise<void> {
		return new Promise((resolve, reject) => {
			// Check if SDK already exists in global scope
			if (window.google?.ima) {
				resolve();
				return;
			}

			const script = document.createElement('script');
			script.async = true;
			script.defer = true;
			script.type = 'text/javascript';
			script.src = this.SDK_URL;

			script.onload = () => resolve();
			script.onerror = () => reject(new Error('Failed to load IMA SDK'));

			const node = document.getElementsByTagName('script')[0];
			node?.parentNode?.insertBefore(script, node);
		});
	}
}
